/**
 * HTML sanitization utility for defense in depth.
 * Note: The HTML content in this app is authored content imported at build time,
 * not user-generated content. This sanitization adds an extra layer of protection.
 */

/** Allowed HTML tags for content rendering */
const ALLOWED_TAGS = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'a', 'span', 'div',
  'code', 'pre', 'blockquote',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'img', 'hr',
]);

/** Allowed attributes by tag */
const ALLOWED_ATTRIBUTES: Record<string, Set<string>> = {
  a: new Set(['href', 'title', 'target', 'rel']),
  img: new Set(['src', 'alt', 'title', 'width', 'height']),
  code: new Set(['class']),
  pre: new Set(['class']),
  span: new Set(['class']),
  div: new Set(['class']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan', 'scope']),
};

/** Dangerous URL patterns */
const DANGEROUS_URL_PATTERNS = [
  /^javascript:/i,
  /^vbscript:/i,
  /^data:/i,
];

/**
 * Check if a URL is safe
 */
function isSafeUrl(url: string): boolean {
  const trimmedUrl = url.trim().toLowerCase();
  return !DANGEROUS_URL_PATTERNS.some(pattern => pattern.test(trimmedUrl));
}

/**
 * Sanitize an HTML attribute value
 */
function sanitizeAttributeValue(tagName: string, attrName: string, value: string): string | null {
  // Check if attribute is allowed for this tag
  const allowedAttrs = ALLOWED_ATTRIBUTES[tagName];
  if (!allowedAttrs || !allowedAttrs.has(attrName)) {
    return null;
  }

  // Special handling for URL attributes
  if (attrName === 'href' || attrName === 'src') {
    if (!isSafeUrl(value)) {
      return null;
    }
  }

  // Special handling for target attribute - ensure rel=noopener
  if (attrName === 'target' && value === '_blank') {
    return value;
  }

  return value;
}

/**
 * Basic HTML sanitizer for defense in depth.
 * Removes dangerous elements and attributes while preserving safe content.
 */
export function sanitizeHtml(html: string): string {
  if (typeof html !== 'string') {
    return '';
  }

  // Create a temporary DOM element to parse HTML
  if (typeof DOMParser === 'undefined') {
    // Server-side or no DOM: return escaped HTML
    return html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Recursively sanitize nodes
  const sanitizeNode = (node: Node): Node | null => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.cloneNode();
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const element = node as Element;
    const tagName = element.tagName.toLowerCase();

    // Check if tag is allowed
    if (!ALLOWED_TAGS.has(tagName)) {
      // Return a document fragment with sanitized children
      const fragment = document.createDocumentFragment();
      Array.from(element.childNodes).forEach(child => {
        const sanitized = sanitizeNode(child);
        if (sanitized) {
          fragment.appendChild(sanitized);
        }
      });
      return fragment;
    }

    // Create new element with only allowed attributes
    const newElement = document.createElement(tagName);

    Array.from(element.attributes).forEach(attr => {
      const sanitizedValue = sanitizeAttributeValue(tagName, attr.name.toLowerCase(), attr.value);
      if (sanitizedValue !== null) {
        newElement.setAttribute(attr.name, sanitizedValue);
      }
    });

    // Add rel="noopener noreferrer" to external links
    if (tagName === 'a' && newElement.getAttribute('target') === '_blank') {
      newElement.setAttribute('rel', 'noopener noreferrer');
    }

    // Recursively sanitize children
    Array.from(element.childNodes).forEach(child => {
      const sanitized = sanitizeNode(child);
      if (sanitized) {
        newElement.appendChild(sanitized);
      }
    });

    return newElement;
  };

  const body = doc.body;
  const fragment = document.createDocumentFragment();

  Array.from(body.childNodes).forEach(child => {
    const sanitized = sanitizeNode(child);
    if (sanitized) {
      fragment.appendChild(sanitized);
    }
  });

  // Convert back to HTML string
  const temp = document.createElement('div');
  temp.appendChild(fragment);
  return temp.innerHTML;
}

/**
 * Check if HTML content appears safe (quick validation)
 * Returns true if content doesn't contain obvious dangerous patterns
 */
export function isHtmlSafe(html: string): boolean {
  if (typeof html !== 'string') {
    return false;
  }

  const dangerousPatterns = [
    /<script\b/i,
    /<iframe\b/i,
    /<object\b/i,
    /<embed\b/i,
    /<form\b/i,
    /\bon\w+\s*=/i, // Event handlers like onclick=, onerror=
    /javascript:/i,
    /vbscript:/i,
    /data:\s*text\/html/i,
  ];

  return !dangerousPatterns.some(pattern => pattern.test(html));
}
